import Axios               from 'axios';
import { DeferredPromise } from '$/lib/utils';

import { Entity }         from '$/entities/BaseEntity';
import { ChatMessage }    from '$/entities/chat/ChatMessage';
import type { FileStore } from '$/entities/FileStore';

import { ChatStreamEventType, ChatThread as ChatThreadCommon } from '$/common/entities/chat/ChatThread';
export * from '$/common/entities/chat/ChatThread';

@Entity('chatThread')
export class ChatThread extends ChatThreadCommon {

	override async addMessage(message: string) {
		// Add the message directly to the thread so it appears in the chat right away
		this.messages.push(ChatMessage.newUserMessage(message));
		if (this.isNew) {
			await this.save();
			return;
		}

		// Then actually submit the message to the server, which makes sure it's a valid message (i.e. not trying to add an assistant message)
		await Axios.post(ChatThread.collectionUrl(`${this.id}/message`), { message });
		await this.reload();
	}

	override async getAIResponse(fileStore?: FileStore) {
		await Axios.get(ChatThread.collectionUrl(`${this.id}/aiResponse`), { params : _.compactObject({ fileStoreId : fileStore?.id }) });
		await this.reload();
	}

	async getAIResponseStream(messageUpdated: () => void, fileStore?: FileStore): Promise<ChatMessage> {
		const deferredPromise = new DeferredPromise<ChatMessage>();
		const eventSource     = new EventSource(ChatThread.collectionUrl(`${this.id}/aiResponse/stream`, { fileStoreId : fileStore?.id }));

		if (_.last(this.messages).isUser) {
			this.messages.push(ChatMessage.newAssistantMessage('', { isStreaming : true }));
		}
		const messageChunks: string[] = [];

		const handleError = (errorMessage: string) => {
			eventSource.close();
			_.last(this.messages).isStreaming = false;
			deferredPromise.reject(new Error(errorMessage));
		};

		eventSource.addEventListener(ChatStreamEventType.MessageChunk, (event: MessageEvent) => {
			messageChunks.push((JSON.parse(event.data)).content || '');
			const currentMessage   = _.last(this.messages);
			currentMessage.message = messageChunks.join('');
			messageUpdated();
		});

		eventSource.addEventListener(ChatStreamEventType.Done, (event: MessageEvent) => {
			eventSource.close();
			deferredPromise.resolve(JSON.parse(event.data).chatMessage);
		});

		eventSource.addEventListener(ChatStreamEventType.Error, (event: MessageEvent) =>
			handleError((JSON.parse(event.data).error || 'Error in stream'))
		);

		eventSource.onerror = error => handleError(error instanceof ErrorEvent ? error.message : 'Stream connection failed');

		return deferredPromise.promise;
	}

	override async removeMessage(index: number) {
		await Axios.delete(ChatThread.collectionUrl(`${this.id}/message/${index}`));
		await this.reload();
	}

	override async clearMessages() {
		await Axios.delete(ChatThread.collectionUrl(`${this.id}/message`));
		await this.reload();
	}

}
